const PENDING_INTENT_KEY = 'pcHasl.routeGuard.pendingIntent'
const PENDING_INTENT_TTL = 10 * 60 * 1000
const ACCESS_TYPES = ['public', 'employee', 'guest', 'business', 'inherit']

function hasText(value) {
  return typeof value === 'string' && value.trim() !== '' && value !== 'null' && value !== 'undefined'
}

function normalizeInternalPath(path) {
  if (!hasText(path) || path.charAt(0) !== '/' || path.indexOf('//') === 0) return null
  if (/^[a-zA-Z][a-zA-Z\d+.-]*:/.test(path) || /[\\?#\u0000-\u001f]/.test(path)) return null
  try {
    const decoded = decodeURIComponent(path)
    if (decoded.indexOf('//') === 0 || /^[a-zA-Z][a-zA-Z\d+.-]*:/.test(decoded) || /[\\?#\u0000-\u001f]/.test(decoded)) return null
  } catch (error) {
    return null
  }
  return path
}

function isRegisteredPath(path, registeredPaths) {
  if (!registeredPaths) return false
  if (typeof registeredPaths.has === 'function') return registeredPaths.has(path)
  if (Array.isArray(registeredPaths)) {
    return registeredPaths.some(item => item && item.path === path)
  }
  return Object.prototype.hasOwnProperty.call(registeredPaths, path)
}

function findAccess(path, routeAccess) {
  const normalizedPath = path === '' ? '/' : path
  if (Array.isArray(routeAccess)) {
    let found = null
    routeAccess.forEach(item => {
      if (!item || item.path !== normalizedPath) return
      const inheritedReview = found && found.reviewRequired === true
      const hasEmployee = (found && found.access === 'employee') || item.access === 'employee'
      found = {
        // 同路径重复声明存在 employee 时采用保守结果，不依赖声明顺序。
        access: hasEmployee ? 'employee' : item.access,
        reviewRequired: inheritedReview || item.reviewRequired === true
      }
    })
    return found
  }
  return routeAccess && routeAccess[normalizedPath] ? routeAccess[normalizedPath] : null
}

function resolveAccess(matched, routeAccess) {
  const records = Array.isArray(matched) ? matched : []
  let resolved = null
  for (let i = 0; i < records.length; i += 1) {
    const record = records[i]
    if (!record) continue
    let metadata = null
    if (record.meta && record.meta.access) {
      metadata = {
        access: record.meta.access,
        reviewRequired: record.meta.reviewRequired === true
      }
    } else {
      metadata = findAccess(record.path, routeAccess)
    }
    if (!metadata) continue
    if (ACCESS_TYPES.indexOf(metadata.access) === -1) return { access: null, reviewRequired: metadata.reviewRequired === true }
    if (!resolved) {
      resolved = {
        access: metadata.access === 'inherit' ? 'employee' : metadata.access,
        reviewRequired: metadata.reviewRequired === true
      }
    } else {
      resolved.access = metadata.access === 'inherit' ? resolved.access : metadata.access
      resolved.reviewRequired = resolved.reviewRequired || metadata.reviewRequired === true
    }
  }
  return resolved || { access: null, reviewRequired: false }
}

function normalizeQueryKey(key) {
  return String(key).replace(/[-_]/g, '').toLowerCase()
}

function isSensitiveQueryKey(key) {
  const normalized = normalizeQueryKey(key)
  return normalized === 'jtl' || normalized === 'sign' || normalized === 'signature' ||
    normalized === 'code' || normalized === 'authorization' || normalized.indexOf('token') !== -1 ||
    normalized.indexOf('credential') !== -1
}

function isNestedReturnQueryKey(key) {
  const normalized = normalizeQueryKey(key)
  return ['redirect', 'redirectpath', 'redirecturl', 'redirecturi', 'return', 'returnurl', 'back', 'backurl', 'target', 'targeturl', 'url', 'next', 'continue', 'to'].indexOf(normalized) !== -1
}

function sanitizeQuery(query, whitelist) {
  const result = {}
  const allowed = Array.isArray(whitelist) ? whitelist : []
  if (!query) return result
  if (typeof query !== 'object' || Array.isArray(query)) return null
  const keys = Object.keys(query)
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i]
    if (isSensitiveQueryKey(key) || isNestedReturnQueryKey(key)) return null
    if (allowed.indexOf(key) === -1) continue
    const value = query[key]
    if (value === undefined || value === null) continue
    if (typeof value === 'object' || typeof value === 'function' || typeof value === 'symbol') return null
    result[key] = String(value)
  }
  return result
}

function createPendingIntent(input, registeredPaths, queryWhitelist, navId, now) {
  if (!input || typeof input !== 'object') return null
  const path = normalizeInternalPath(input.path)
  if (!path || !isRegisteredPath(path, registeredPaths) || !hasText(navId)) return null
  const whitelist = typeof queryWhitelist === 'function' ? queryWhitelist(path) : queryWhitelist
  const query = sanitizeQuery(input.query, whitelist)
  if (!query) return null
  return {
    path: path,
    query: query,
    realm: 'employee',
    navId: navId,
    createdAt: typeof now === 'number' ? now : Date.now(),
    replayed: false
  }
}

function validatePendingIntent(intent, registeredPaths, queryWhitelist, now) {
  if (!intent || intent.realm !== 'employee' || intent.replayed !== false || !hasText(intent.navId)) return null
  if (typeof intent.createdAt !== 'number' || !isFinite(intent.createdAt)) return null
  const currentTime = typeof now === 'number' ? now : Date.now()
  if (intent.createdAt > currentTime || currentTime - intent.createdAt > PENDING_INTENT_TTL) return null
  return createPendingIntent(intent, registeredPaths, queryWhitelist, intent.navId, intent.createdAt)
}

function clearPendingIntent(storage) {
  if (!storage || typeof storage.removeItem !== 'function') return
  try {
    storage.removeItem(PENDING_INTENT_KEY)
  } catch (error) {
    // sessionStorage 不可用时仅放弃回跳，不阻断安全出口。
  }
}

function savePendingIntent(storage, intent) {
  if (!storage || !intent || typeof storage.setItem !== 'function') return false
  try {
    storage.setItem(PENDING_INTENT_KEY, JSON.stringify(intent))
    return true
  } catch (error) {
    return false
  }
}

function consumePendingIntent(storage, registeredPaths, queryWhitelist, now) {
  if (!storage || typeof storage.getItem !== 'function') return null
  let raw = null
  try {
    raw = storage.getItem(PENDING_INTENT_KEY)
  } catch (error) {
    return null
  }
  if (!raw) return null
  clearPendingIntent(storage)
  try {
    return validatePendingIntent(JSON.parse(raw), registeredPaths, queryWhitelist, now)
  } catch (error) {
    return null
  }
}

function getOrigin(locationLike) {
  if (!locationLike) return null
  const origin = locationLike.origin || ((locationLike.protocol || '') + '//' + (locationLike.host || ''))
  return /^https?:\/\/[^/]+$/i.test(origin) ? origin : null
}

function buildInternalReturnUrl(input, locationLike, address, registeredPaths, queryWhitelist) {
  const origin = getOrigin(locationLike)
  const basePath = normalizeInternalPath((address || '').split('#')[0])
  const target = typeof input === 'string' ? { path: input, query: {} } : input
  if (!origin || !basePath || !target) return null
  const path = normalizeInternalPath(target.path)
  if (!path || !isRegisteredPath(path, registeredPaths)) return null
  const whitelist = typeof queryWhitelist === 'function' ? queryWhitelist(path) : queryWhitelist
  const query = sanitizeQuery(target.query, whitelist)
  if (!query) return null
  const queryString = Object.keys(query).map(key => encodeURIComponent(key) + '=' + encodeURIComponent(query[key])).join('&')
  return origin + basePath + '#' + path + (queryString ? '?' + queryString : '')
}

function buildSsoLoginUrl(target, locationLike, config, registeredPaths, queryWhitelist) {
  if (!config || !hasText(config.getst) || !hasText(config.loginBack)) return null
  const returnUrl = buildInternalReturnUrl(target, locationLike, config.address, registeredPaths, queryWhitelist)
  const basePath = normalizeInternalPath((config.address || '').split('#')[0])
  const origin = getOrigin(locationLike)
  if (!returnUrl || !basePath || !origin) return null
  return config.getst + encodeURIComponent(config.loginBack + encodeURIComponent(returnUrl)) +
    '&loginUrl=' + encodeURIComponent(origin + basePath)
}

function resolveTitle(matched) {
  const records = Array.isArray(matched) ? matched : []
  let title = ''
  records.forEach(record => {
    if (record && record.meta && hasText(record.meta.title)) title = record.meta.title
  })
  return title
}

function isSameNavigation(to, from) {
  return !!(to && from && from.matched && from.matched.length && to.fullPath === from.fullPath)
}

function once(callback) {
  let called = false
  return function (value) {
    if (called) return false
    called = true
    callback(value)
    return true
  }
}

function resolveCredentialDomain(access) {
  if (access === 'business') return 'business'
  if (access === 'public' || access === 'guest') return 'untoken'
  return 'employee'
}

function shouldRedirectEmployeeLogin(status, credentialDomain) {
  return status === 401 && credentialDomain === 'employee'
}

export default {
  PENDING_INTENT_KEY: PENDING_INTENT_KEY,
  PENDING_INTENT_TTL: PENDING_INTENT_TTL,
  hasText: hasText,
  normalizeInternalPath: normalizeInternalPath,
  isRegisteredPath: isRegisteredPath,
  findAccess: findAccess,
  resolveAccess: resolveAccess,
  sanitizeQuery: sanitizeQuery,
  createPendingIntent: createPendingIntent,
  validatePendingIntent: validatePendingIntent,
  clearPendingIntent: clearPendingIntent,
  savePendingIntent: savePendingIntent,
  consumePendingIntent: consumePendingIntent,
  buildInternalReturnUrl: buildInternalReturnUrl,
  buildSsoLoginUrl: buildSsoLoginUrl,
  resolveTitle: resolveTitle,
  isSameNavigation: isSameNavigation,
  once: once,
  resolveCredentialDomain: resolveCredentialDomain,
  shouldRedirectEmployeeLogin: shouldRedirectEmployeeLogin
}



// WEBPACK FOOTER //
// ./src/router/policy.js